import { useState, useEffect } from 'react';
import { Tooltip, LeftTooltip } from 'ui/tooltip';
import { PrimaryButton, SecondaryButton } from 'ui/button';
import { FlexSpacer } from 'ui/flexSpacer';
import { UuidProjectType } from 'core/userContext';
import { InputText } from 'ui/inputs';
import { noop } from 'utils/noop';
import { Radio } from 'ui/radio';
import {
  MISSING_MANAGE_CATALOGS_PERMISSION,
  MISSING_USER_PROFILES_DATA_PERIMISSIONS,
} from 'modules/catalog/catalogConstants';
import { PreviewResultsBarContainer, PreviewRadioButtonAndLabel } from './PreviewResultsBar.styled';
import { shouldDisablePreviewButton } from './utils';
import type { PreviewResultsBarProps } from './types';

const canSelectUserIdentifierType = (uuidProjectType: UuidProjectType, isUuidBased: boolean) =>
  isUuidBased && uuidProjectType === UuidProjectType.EmailUnique;

export const PreviewResultsBar = ({
  canManageCatalogs,
  canManageUserProfilesAndData,
  canSave,
  children,
  disabled,
  hasDynamicFields,
  isLoading,
  isUuidBased,
  onClickPreview = noop,
  onClickSave = noop,
  orderByDynamicLocation,
  previewIdentifier = '',
  setPreviewIdentifier = noop,
  usesSmartRecos,
  uuidProjectType,
}: PreviewResultsBarProps) => {
  const [isUsingEmailPreviewIdentifier, setIsUsingEmailPreviewIdentifier] = useState(
    !isUuidBased || uuidProjectType === UuidProjectType.EmailUnique,
  );

  useEffect(() => {
    setIsUsingEmailPreviewIdentifier(
      !isUuidBased || uuidProjectType === UuidProjectType.EmailUnique,
    );
  }, [isUuidBased, uuidProjectType]);

  const handleClick = () => {
    onClickPreview(previewIdentifier.trim(), isUsingEmailPreviewIdentifier);
  };
  const needsPreviewIdentifier =
    usesSmartRecos || hasDynamicFields || orderByDynamicLocation !== undefined;
  const previewButtonLabel = canSave ? 'Save and Preview' : 'Preview Results';
  let saveButtonLabel;
  if (hasDynamicFields) {
    saveButtonLabel = 'Save Only';
  } else {
    saveButtonLabel = canSave ? 'Save Only' : undefined;
  }

  return (
    <PreviewResultsBarContainer>
      {needsPreviewIdentifier && <label>Preview with sample data from</label>}
      <FlexSpacer justifyContent="flex-end" gap="actionGap">
        {needsPreviewIdentifier && (
          <Tooltip
            tooltipContent={MISSING_USER_PROFILES_DATA_PERIMISSIONS}
            isEnabled={!canManageUserProfilesAndData}
          >
            <FlexSpacer>
              {canSelectUserIdentifierType(uuidProjectType, isUuidBased) && (
                <>
                  <PreviewRadioButtonAndLabel>
                    <Radio
                      value="previewIdentifier"
                      id="preview-identifier--email"
                      isChecked={isUsingEmailPreviewIdentifier}
                      onClick={() => setIsUsingEmailPreviewIdentifier(true)}
                    />
                    <label htmlFor="#preview-identifier--email">Email</label>
                  </PreviewRadioButtonAndLabel>
                  <PreviewRadioButtonAndLabel>
                    <Radio
                      value="previewIdentifier"
                      id="preview-identifier--userid"
                      isChecked={!isUsingEmailPreviewIdentifier}
                      onClick={() => setIsUsingEmailPreviewIdentifier(false)}
                    />
                    <label htmlFor="#preview-identifier--userid">User ID</label>
                  </PreviewRadioButtonAndLabel>
                </>
              )}
              <InputText
                isDisabled={!canManageUserProfilesAndData}
                data-test="collections-preview-identifier-input"
                onChange={(value) => setPreviewIdentifier(value)}
                placeholder={isUsingEmailPreviewIdentifier ? 'Contact Email' : 'User ID'}
                value={previewIdentifier}
              />
            </FlexSpacer>
          </Tooltip>
        )}
        <Tooltip
          isEnabled={canSave && !canManageCatalogs}
          tooltipContent={MISSING_MANAGE_CATALOGS_PERMISSION}
          isUsedOnButton
        >
          <PrimaryButton
            data-test="collections-save-and-preview"
            isDisabled={
              (canSave && !canManageCatalogs) ||
              shouldDisablePreviewButton({
                disabled,
                hasDynamicFields,
                orderByDynamicLocation,
                previewIdentifier,
                usesSmartRecos,
              }) ||
              isLoading
            }
            onClick={handleClick}
          >
            {previewButtonLabel}
          </PrimaryButton>
        </Tooltip>
        {saveButtonLabel && (
          <LeftTooltip
            isUsedOnButton
            tooltipContent={MISSING_MANAGE_CATALOGS_PERMISSION}
            isEnabled={canSave && !canManageCatalogs}
          >
            <SecondaryButton
              data-test="collections-save-only"
              isDisabled={canSave && !canManageCatalogs}
              onClick={onClickSave}
            >
              {saveButtonLabel}
            </SecondaryButton>
          </LeftTooltip>
        )}
        {children}
      </FlexSpacer>
    </PreviewResultsBarContainer>
  );
};
